import { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import { deviceAtom } from "recoil/deviceAtom";
import { sendMessage } from "utils/webview";
import useIsInappWebview from "hooks/useIsInappWebview";
import CustomModal from "components/common/CustomModal";
import {
  hasAskedHomePushPrompt,
  isHomePushPromptTargetUser,
  markHomePushPromptAsked,
  shouldShowHomePushPrompt,
} from "./homePushPrompt";

// 지정된 userId 사용자에게 홈 진입 시 푸시 권한 안내 팝업을 1회 노출한다.
// 이미 권한을 허용했거나 한 번 이상 물어본 적이 있으면 노출하지 않는다.
// 팝업 UI/브리지는 FeedbackCell 의 소감 전송 후 안내(알림 켜기)와 동일하다.
export default function HomePushPermissionPrompt() {
  const userState = useRecoilValue(userAtom);
  const isCoach = userState?.role !== "patient";
  const userId = userState?.userId;
  const isTargetUser = isHomePushPromptTargetUser(userId);

  const [deviceState, setDeviceState] = useRecoilState(deviceAtom);
  const isInappWebview = useIsInappWebview();

  const [isPushModalOpen, setIsPushModalOpen] = useState<boolean>(false);
  const [hasShownPushModal, setHasShownPushModal] = useState<boolean>(false);

  // 대상 사용자면서 푸시 권한 상태를 모르면 앱에 상태 조회 요청 (응답은 Settings 가 처리)
  useEffect(() => {
    if (
      isInappWebview &&
      !isCoach &&
      isTargetUser &&
      deviceState.pushNotificationStatus === undefined
    ) {
      sendMessage(JSON.stringify({ event: "get_pushNotification_status" }));
    }
  }, [
    isInappWebview,
    isCoach,
    isTargetUser,
    deviceState.pushNotificationStatus,
  ]);

  // 홈 진입 시: 조건을 만족하면 안내 팝업 노출.
  // 노출 시점에 "물어봄"을 기록하여 재진입/재시작 후에도 다시 묻지 않는다. (계정당 최대 1회)
  useEffect(() => {
    if (hasShownPushModal || userId === undefined) return;
    if (
      !shouldShowHomePushPrompt({
        isInappWebview,
        isCoach,
        userId,
        pushNotificationStatus: deviceState.pushNotificationStatus,
        hasAsked: hasAskedHomePushPrompt(userId),
      })
    ) {
      return;
    }
    setHasShownPushModal(true);
    markHomePushPromptAsked(userId);
    setIsPushModalOpen(true);
  }, [
    hasShownPushModal,
    isInappWebview,
    isCoach,
    userId,
    deviceState.pushNotificationStatus,
  ]);

  if (!isInappWebview || isCoach || !isTargetUser) return null;

  return (
    <CustomModal
      open={isPushModalOpen}
      onClose={() => setIsPushModalOpen(false)}
      title={"알림을 켜주세요"}
      subtitle={"코치 선생님의 채팅 메시지를 푸시 알림으로 알려드려요."}
      actions={[
        {
          label: "알림 허용하기",
          variant: "Solid",
          color: "primary",
          onClick: () => {
            sendMessage(
              JSON.stringify({
                event: "request_pushNotification",
                params: {
                  enableGoToSetting: true,
                  disableRequest: false,
                },
              })
            );
            setDeviceState((prev) => ({
              ...prev,
              pushNotificationStatus: "not set",
            }));
            setIsPushModalOpen(false);
          },
        },
        {
          label: "나중에 하기",
          variant: "Plain",
          onClick: () => setIsPushModalOpen(false),
        },
      ]}
    />
  );
}
